import React, { useRef, useEffect } from "react";
import { GridProps, Grid } from "../grid/grid";
import { CalendarProps, Calendar } from "../calendar/calendar";
import { TaskGanttContentProps, TaskGanttContent } from "./task-gantt-content";
import styles from "./gantt.module.css";

export type TaskGanttProps = {
  gridProps: GridProps;
  calendarProps: CalendarProps;
  barProps: TaskGanttContentProps;
  ganttHeight: number;
  scrollY: number;
  scrollX: number;
};
export const TaskGantt: React.FC<TaskGanttProps> = ({
  gridProps,
  calendarProps,
  barProps,
  ganttHeight,
  scrollY,
  scrollX,
}) => {
  const ganttSVGRef = useRef<SVGSVGElement>(null);
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const verticalGanttContainerRef = useRef<HTMLDivElement>(null);
  const newBarProps = { ...barProps, svg: ganttSVGRef };

  // Pan/drag state for viewport dragging when starting outside task bars
  const panState = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });
  // RAF handle to throttle dispatching scroll updates while dragging
  const rafRef = useRef<number | null>(null);

  // Autoscroll state for edge-triggered scrolling during task drag
  const autoscrollStateRef = useRef<{ active: boolean; direction: string | null; step: number; lastScrollTime: number }>({ 
    active: false,
    direction: null, 
    step: 0,
    lastScrollTime: 0
  });
  const autoscrollRafRef = useRef<number | null>(null);

  // RAF loop for autoscroll during task drag when mouse approaches edges
  useEffect(() => {
    const container = verticalGanttContainerRef.current;
    if (!container) return;

    const scroll = () => {
      if (!autoscrollStateRef.current.active || !container) {
        autoscrollRafRef.current = null;
        return;
      }

      const now = Date.now();
      const delay = 150; // Delay in milliseconds between scroll steps

      // Only scroll if enough time has passed since last scroll
      if (now - autoscrollStateRef.current.lastScrollTime < delay) {
        autoscrollRafRef.current = window.requestAnimationFrame(scroll);
        return;
      }

      const maxLeft = Math.max(0, gridProps.svgWidth - container.clientWidth);
      let newLeft = container.scrollLeft;

      if (autoscrollStateRef.current.direction === 'left') {
        newLeft = Math.max(0, container.scrollLeft - autoscrollStateRef.current.step);
      } else if (autoscrollStateRef.current.direction === 'right') {
        newLeft = Math.min(maxLeft, container.scrollLeft + autoscrollStateRef.current.step);
      }

      if (newLeft !== container.scrollLeft) {
        container.scrollLeft = newLeft;
        autoscrollStateRef.current.lastScrollTime = now;

        // Dispatch scroll update for parent to sync React state
        window.dispatchEvent(
          new CustomEvent('gantt-scroll-update', {
            detail: { scrollX: container.scrollLeft, scrollY: horizontalContainerRef.current?.scrollTop || 0 }
          })
        );
      }

      autoscrollRafRef.current = window.requestAnimationFrame(scroll);
    };

    const onAutoscrollStart = (e: Event) => {
      const evt = e as CustomEvent<{ direction: string; step: number }>;
      autoscrollStateRef.current.active = true;
      autoscrollStateRef.current.direction = evt.detail.direction;
      autoscrollStateRef.current.step = evt.detail.step;
      autoscrollStateRef.current.lastScrollTime = 0; // Reset timer to scroll immediately

      if (autoscrollRafRef.current === null) {
        autoscrollRafRef.current = window.requestAnimationFrame(scroll);
      }
    };

    const onAutoscrollStop = () => {
      autoscrollStateRef.current.active = false;
      if (autoscrollRafRef.current !== null) {
        window.cancelAnimationFrame(autoscrollRafRef.current);
        autoscrollRafRef.current = null;
      }
    };

    window.addEventListener('gantt-autoscroll-start', onAutoscrollStart);
    window.addEventListener('gantt-autoscroll-stop', onAutoscrollStop);
    return () => {
      window.removeEventListener('gantt-autoscroll-start', onAutoscrollStart);
      window.removeEventListener('gantt-autoscroll-stop', onAutoscrollStop);
      onAutoscrollStop();
    };
  }, [verticalGanttContainerRef, horizontalContainerRef, gridProps.svgWidth]);

  // Attach mousedown on the vertical container to start panning when clicking outside task bars
  useEffect(() => {
    const container = verticalGanttContainerRef.current;
    const horizontal = horizontalContainerRef.current;
    if (!container || !horizontal) return;

    const onMouseDown = (e: MouseEvent) => {
      // start only on left button
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      // if clicked inside a task bar or arrows, do not start view pan (allow existing interactions)
      if (target && target.closest && (target.closest('.bar') || target.closest('.arrows') || target.closest('g.bar'))) {
        return;
      }

      panState.current.active = true;
      panState.current.startX = e.clientX;
      panState.current.startY = e.clientY;
      panState.current.startScrollLeft = container.scrollLeft;
      panState.current.startScrollTop = horizontal.scrollTop;
      container.style.cursor = 'grabbing';

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      e.preventDefault();
    };

    const onMouseMove = (ev: MouseEvent) => {
      if (!panState.current.active) return;
      const dx = ev.clientX - panState.current.startX;
      const dy = ev.clientY - panState.current.startY;
      const newLeft = panState.current.startScrollLeft - dx;
      const newTop = panState.current.startScrollTop - dy;
      // clamp
      const maxLeft = Math.max(0, gridProps.svgWidth - 0);
      const ganttFullHeight = barProps.tasks.length * barProps.rowHeight;
      const maxTop = Math.max(0, ganttFullHeight - (ganttHeight || ganttFullHeight));
      container.scrollLeft = Math.max(0, Math.min(newLeft, maxLeft));
      horizontal.scrollTop = Math.max(0, Math.min(newTop, maxTop));
      // throttle update events to RAF to keep React state in sync during drag
      if (rafRef.current === null) {
        rafRef.current = window.requestAnimationFrame(() => {
          rafRef.current = null;
          const detail = { scrollX: container.scrollLeft, scrollY: horizontal.scrollTop };
          window.dispatchEvent(new CustomEvent('gantt-scroll-update', { detail }));
        });
      }
    };

    const onMouseUp = () => {
      if (!panState.current.active) return;
      panState.current.active = false;
      container.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      // cancel any pending RAF and immediately dispatch final positions so parent can sync React state
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const detail = { scrollX: container.scrollLeft, scrollY: horizontal.scrollTop };
      window.dispatchEvent(new CustomEvent('gantt-scroll-update', { detail }));
    };

    container.addEventListener('mousedown', onMouseDown);
    return () => {
      container.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [verticalGanttContainerRef, horizontalContainerRef, gridProps.svgWidth, barProps.tasks.length, barProps.rowHeight, ganttHeight]);

  // Cursor hint: show grab cursor when hovering empty gantt space; respect task bars/arrows
  useEffect(() => {
    const container = verticalGanttContainerRef.current;
    if (!container) return;

    const pointerMove = (e: MouseEvent) => {
      // don't change cursor while actively panning
      if (panState.current.active) return;
      const target = e.target as HTMLElement | null;
      if (target && target.closest && (target.closest('.bar') || target.closest('.arrows') || target.closest('g.bar'))) {
        container.style.cursor = '';
      } else {
        container.style.cursor = 'grab';
      }
    };

    const onMouseLeave = () => {
      if (!panState.current.active) container.style.cursor = '';
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        // show grab cursor as hint when space is held
        if (!panState.current.active) container.style.cursor = 'grab';
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        // restore cursor based on current pointer location
        container.style.cursor = '';
      }
    };

    window.addEventListener('mousemove', pointerMove);
    container.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('mousemove', pointerMove);
      container.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      if (!panState.current.active) container.style.cursor = '';
    };
  }, [verticalGanttContainerRef]);

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  useEffect(() => {
    if (verticalGanttContainerRef.current) {
      verticalGanttContainerRef.current.scrollLeft = scrollX;
    }
  }, [scrollX]);

  return (
    <div
      className={styles.ganttVerticalContainer}
      ref={verticalGanttContainerRef}
      dir="ltr"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={gridProps.svgWidth}
        height={calendarProps.headerHeight}
        fontFamily={barProps.fontFamily}
      >
        <Calendar {...calendarProps} />
      </svg>
      <div
        ref={horizontalContainerRef}
        className={styles.horizontalContainer}
        style={
          ganttHeight
            ? { height: ganttHeight, width: gridProps.svgWidth }
            : { width: gridProps.svgWidth }
        }
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={gridProps.svgWidth}
          height={barProps.rowHeight * barProps.tasks.length}
          fontFamily={barProps.fontFamily}
          ref={ganttSVGRef}
        >
          <Grid {...gridProps} />
          <TaskGanttContent {...newBarProps} />
        </svg>
      </div>
    </div>
  );
};
